import { usePurchaseOrderStore } from '../../../../store/PurchaseOrderStore';
import { Button, Form, Table, Tooltip } from 'antd';
import { useState } from 'react';
import { useFetchData } from '../../../../hooks';
import {
  BaseModel,
  FormField,
  PageableResponse,
  ProductModel,
  toProductMode,
} from '../../../../types';
import { Resource } from '../../../../lib/enums';
import { OrderLineForm } from '../../../../types/PurchaseOrder';
import WarningModal from '../../../../components/ui/modal/WarningModal';
import InfoModal from '../../../../components/ui/modal/InfoModal';
import ModalForm from '../../../../components/ui/ModalForm';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';

const OrderLineFormEditPage = () => {
  const [form] = Form.useForm();
  const purchaseOrderStore = usePurchaseOrderStore();

  const [modalVisible, setModalVisible] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [uuidDelete, setUuidDelete] = useState('');

  const { data: productData, isLoading: isProductLoading } = useFetchData<
    PageableResponse<ProductModel>
  >('/product/products', {
    resource: Resource.FULL,
    length: parseInt('999999'),
  });
  const { data: uomData, isLoading: isUomLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/product/uoms', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const fields: FormField[] = [
    {
      name: 'product',
      label: 'Product',
      rules: [{ required: true, message: 'Please select product' }],
      isMultiple: false,
      type: 'select',
      isLoading: isProductLoading,
      values: productData?.data,
      defaultValue: purchaseOrderStore.orderLine?.product?.id,
    },
    {
      name: 'productUom',
      label: 'UoM',
      isMultiple: false,
      type: 'select',
      values: uomData?.data,
      isLoading: isUomLoading,
      defaultValue: purchaseOrderStore.orderLine?.productUom?.id,
    },
    {
      name: 'unitPrice',
      label: 'Unit Price',
      rules: [{ required: true, message: 'Please enter quantity' }],
      defaultValue: purchaseOrderStore.orderLine?.unitPrice,
    },
    {
      name: 'quantity',
      label: 'Quantity',
      type: 'numeric',
      rules: [{ required: true, message: 'Please enter quantity' }],
      defaultValue: purchaseOrderStore.orderLine?.quantity,
    },
  ];

  const handleOpenModal = () => {
    setModalVisible(true);
    form.resetFields();
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleEditButtonClick = (record: OrderLineForm) => {
    setModalVisible(true);
    purchaseOrderStore.setOrderLine(record);
  };
  const handleDeleteButtonClick = (record: OrderLineForm) => {
    setUuidDelete(record.uuid);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteModalConfirm = () => {
    purchaseOrderStore.removeOrderLine(uuidDelete);
    setIsDeleteModalOpen(false);
    setIsInfoModalOpen(true);
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
  };

  const onValuesChange = (changedFields: any) => {
    if (changedFields.product) {
      const product: ProductModel | undefined = productData?.data?.find(
        (obj) => {
          return obj.id == changedFields.product;
        },
      );
      form.setFieldValue('productUom', product?.defaultUom?.id);
    }
  };

  const onFinish = (value: any) => {
    let product: BaseModel | undefined = productData?.data?.find((obj) => {
      return obj.id == value.product;
    });
    let productUom: BaseModel | undefined = uomData?.data?.find((obj) => {
      return obj.id == value.productUom;
    });

    if (product === undefined) {
      const recProduct = purchaseOrderStore.formData.orderLines.map((item) =>
        toProductMode(item.product!),
      );

      product = recProduct.find((obj) => {
        return obj.id == value.product;
      });
    }

    if (productUom === undefined) {
      const recUoM = purchaseOrderStore.formData.orderLines.map(
        (item) => item.productUom!,
      );

      productUom = recUoM.find((obj) => {
        return obj.id == value.productUom;
      });
    }

    const orderLine = {
      ...purchaseOrderStore.orderLine,
      ...value,
      id: purchaseOrderStore.orderLine?.id,
      product: product,
      productUom: productUom,
      quantity: value.quantity,
      totalAmount: value.totalAmount,
      unitPrice: value.unitPrice,
      uuid:
        purchaseOrderStore?.orderLine?.uuid === undefined ||
        purchaseOrderStore?.orderLine?.uuid === null ||
        purchaseOrderStore?.orderLine?.uuid === ''
          ? ''
          : purchaseOrderStore.orderLine?.uuid,
    };

    if (
      orderLine.uuid === undefined ||
      orderLine.uuid === null ||
      orderLine.uuid === ''
    ) {
      purchaseOrderStore.addOrderLine(orderLine);
    } else {
      purchaseOrderStore.editOrderLine(orderLine.uuid, orderLine);
    }
    setModalVisible(false);
  };

  return (
    <>
      <div className="py-2 px-4 flex justify-between">
        <div className={'text-base font-semibold leading-6'}>Order Lines</div>
        <Button onClick={handleOpenModal}>Add Order</Button>
      </div>
      <div className="p-4">
        <Table
          rowKey={(record) => record.uuid}
          dataSource={purchaseOrderStore.formData.orderLines}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        >
          <Table.Column
            title="Product"
            dataIndex="product"
            render={(_text: string, record: OrderLineForm) => {
              return <span>{record.product?.description}</span>;
            }}
          />
          <Table.Column
            title="Code"
            dataIndex="productCode"
            render={(_text: string, record: OrderLineForm) => {
              return <span>{record.product?.code}</span>;
            }}
          />
          <Table.Column
            title="Product UoM"
            dataIndex="productUoM"
            render={(_text: string, record: OrderLineForm) => {
              return <span>{record.productUom?.description}</span>;
            }}
          />
          <Table.Column title="Quantity" dataIndex="quantity" />
          <Table.Column title="Unit Price" dataIndex="unitPrice" />
          <Table.Column
            title="Total Amount"
            dataIndex="totalAmount"
            render={(_text: string, record: OrderLineForm) => {
              return <span>{record.quantity * record.unitPrice}</span>;
            }}
          />
          <Table.Column
            title="Action"
            key="operation"
            fixed="right"
            render={(record: OrderLineForm) => {
              return (
                <div className="space-x-1">
                  <Tooltip title="Edit">
                    <Button
                      type="text"
                      icon={<EditOutlined style={{ color: '#4080fd' }} />}
                      size="middle"
                      onClick={() => handleEditButtonClick(record)}
                    />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="middle"
                      danger
                      onClick={() => handleDeleteButtonClick(record)}
                    />
                  </Tooltip>
                </div>
              );
            }}
          />
        </Table>
      </div>

      <WarningModal
        message={
          'This action cannot be undone and all associated data will be permanently removed from our system.'
        }
        onClose={handleDeleteModalClose}
        visible={isDeleteModalOpen}
        title={'Remove Order line item?'}
        onConfirm={handleDeleteModalConfirm}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={'Order line item successfully removed'}
        onClose={handleInfoModalClose}
      />

      <ModalForm
        visible={modalVisible}
        onClose={handleCloseModal}
        formFields={fields}
        rowCol={2}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
        data={purchaseOrderStore.orderLine}
        setData={purchaseOrderStore.setOrderLine}
        form={form}
      />
    </>
  );
};

export default OrderLineFormEditPage;
